import * as React from "react";
import useOnClickOutside from "ui/hooks/useOnClickOutside";
import { cx } from "utils/join-classnames";
import { Button } from "ui/components";
import styles from "./DateSelection.module.scss";

interface Props {
    timeStart: Date;
    timeEnd: Date;
    selectedDay: Date;
    selectedTime: Date;
    onChange(newTime: Date): void;
}

const TimePicker: React.FC<Props> = ({
    timeStart,
    timeEnd,
    selectedDay,
    selectedTime = new Date(),
    onChange,
}) => {
    const [isOpened, setIsOpened] = React.useState<boolean>(false);
    const timePickerRef = React.useRef<HTMLDivElement>(null);
    useOnClickOutside(timePickerRef, () => setIsOpened(false));

    const inputElementRef = React.useRef<HTMLInputElement>(null);
    const listElementRef = React.useRef<HTMLDivElement>(null);
    const [inputValue, setInputValue] = React.useState<string>(
        selectedTime.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
        }),
    );
    const [inputError, setInputError] = React.useState<boolean>(false);

    React.useEffect(() => {
        setInputValue(
            selectedTime.toLocaleTimeString("en-GB", {
                hour: "2-digit",
                minute: "2-digit",
            }),
        );
    }, [selectedTime]);

    // Sanitize the props, we only care about the time so we always use today
    // to make everything else easier to calculate.
    const dateStart = new Date();
    dateStart.setHours(timeStart.getHours(), timeStart.getMinutes(), 0, 0);
    const dateEnd = new Date();
    dateEnd.setHours(timeEnd.getHours(), timeEnd.getMinutes(), 0, 0);

    const handleContainerClick = () => {
        setIsOpened(!isOpened);
    };

    const generateTimeList = (minutesPerStep = 30): Date[] => {
        const list: Date[] = [];
        for (let i = 0; i < 1440; i += minutesPerStep) {
            const hours = Math.floor(i / 60);
            const minutes = i % 60;
            const date = new Date(selectedDay);
            date.setHours(hours, minutes, 0, 0);
            list.push(date);
        }
        return list;
    };
    const timeList: Date[] = React.useMemo(generateTimeList, [selectedDay]);

    React.useEffect(() => {
        const listElement = listElementRef.current;
        if (!listElement) {
            return;
        }
        if (isOpened) {
            let targetIndex = 0;
            for (let i = 0; i < timeList.length; i += 1) {
                const date = timeList[i];
                if (date > selectedTime) {
                    break;
                }
                targetIndex = i;
            }
            const listItem = listElement.children[targetIndex] as HTMLLIElement;
            if (listItem) {
                listElement.scrollTop = listItem.offsetTop;
            }
        }
    }, [isOpened]);

    const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const newValue = e.target.value;
        setInputValue(newValue);
        const re = new RegExp("^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$");
        if (!re.test(newValue)) {
            setInputError(true);
            return;
        }
        setInputError(false);
        const finalTime = new Date(selectedTime);
        finalTime.setHours(
            +newValue.split(":")[0],
            +newValue.split(":")[1],
            0,
            0,
        );
        onChange(finalTime);
    };

    const setTime = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        const target = event.target as HTMLLIElement;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const time = new Date(parseInt(target.dataset.time!, 10));
        // Align the changed time to whatever date the parent expects
        const finalTime = new Date(selectedTime);
        finalTime.setHours(time.getHours(), time.getMinutes(), 0, 0);
        onChange(finalTime);
        setIsOpened(false);
    };

    return (
        <>
            <Button
                type="secondary"
                theme="light"
                icon="time"
                className={styles.inputContainer}
                onClick={handleContainerClick}
                tabIndex={0}
            >
                <input
                    type="text"
                    name="time"
                    className={cx(
                        styles.input,
                        inputError ? styles.error : null,
                    )}
                    value={inputValue}
                    onChange={handleTimeChange}
                    ref={inputElementRef}
                />
            </Button>
            {isOpened && (
                <div className={styles.listContainer} ref={timePickerRef}>
                    <div className={styles.list} ref={listElementRef}>
                        {timeList.map((time) => {
                            const isAvailable =
                                timeStart < time && timeEnd > time;
                            const className = cx(
                                selectedTime === time ? styles.current : null,
                                isAvailable ? null : styles.disabled,
                            );
                            return (
                                <div
                                    key={time.getTime()}
                                    onClick={setTime}
                                    data-time={time.getTime()}
                                    className={className}
                                    role="presentation"
                                    tabIndex={-1}
                                >
                                    {time.toLocaleTimeString("en-GB", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </>
    );
};

export default TimePicker;
